import type { ProblemJSDependencies } from '../problemJSDependencies';
import { BasicObject } from '../uiObjects/basicObject';

import {
  BORDER_WIDTH,
  HelpAnswerRegion,
  getHelpAnswerRegionLabelMetrics,
} from './HelpAnswerRegion';
import { LABEL_FONT_SIZE } from './SectionedHelpConstants';

const SINGLE_COLUMN_ANSWER_LABEL_HEIGHT = LABEL_FONT_SIZE;
const SINGLE_COLUMN_ANSWER_PADDING = 10;
const MINIMAL_SINGLE_COLUMN_ANSWER_PADDING = BORDER_WIDTH + 1;

// Any kind of help section that is not a TwoColumnHelpSection
export interface StaticPanelHelpSectionDTO {
  label?: string;
  body?: string;
  lastPanelIsAnswer?: boolean;
  panels?: unknown[];
  vertPSS?: boolean;
  horizontalAlignment?: string;
}

// This class is just a way to deliver any static panel, along with optional answer graphics.
export class StaticPanelHelpSection extends BasicObject {
  constructor(
    problemJS: ProblemJSDependencies,
    dto: StaticPanelHelpSectionDTO,
    maxHelpWidth: number,
    showAnswerRegion: boolean,
    answerIndex?: number
  ) {
    super();
    this.gmmName = 'staticPanelHelpSection';

    const staticPanel = problemJS.getStaticPanel(
      dto,
      0,
      0,
      undefined,
      undefined,
      maxHelpWidth
    );

    if (showAnswerRegion) {
      addSingleColumnAnswerRegion(
        problemJS,
        staticPanel,
        dto.horizontalAlignment === 'LEFT',
        maxHelpWidth,
        answerIndex
      );
    }

    this.add(staticPanel);
    this.setAllDim(staticPanel.viewportW, staticPanel.viewportH);
  }

  static hasAnswer(dto: StaticPanelHelpSectionDTO): boolean {
    return (
      dto.lastPanelIsAnswer === true &&
      dto.vertPSS === true &&
      Array.isArray(dto.panels) &&
      dto.panels.length > 0
    );
  }
}

const addSingleColumnAnswerRegion = (
  problemJS: ProblemJSDependencies,
  section: BasicObject,
  isLeftAligned: boolean,
  maxHelpWidth: number,
  answerIndex?: number
): void => {
  const answerPanel = section.children[section.children.length - 1];

  if (!answerPanel) {
    return;
  }

  const answerPadding = SINGLE_COLUMN_ANSWER_PADDING;
  const originalSectionWidth = section.viewportW;
  const labelMetrics = getHelpAnswerRegionLabelMetrics(
    problemJS.ctx,
    answerIndex
  );
  const answerRegionWidth = Math.min(
    maxHelpWidth,
    Math.max(answerPanel.viewportW + 2 * answerPadding, labelMetrics.outerWidth)
  );
  const maxAnswerPanelWidth = Math.max(
    0,
    answerRegionWidth - 2 * MINIMAL_SINGLE_COLUMN_ANSWER_PADDING
  );

  if (answerPanel.viewportW > maxAnswerPanelWidth) {
    answerPanel.setMaxWidth(maxAnswerPanelWidth);
  }

  const answerRegionHeight =
    answerPanel.viewportH +
    SINGLE_COLUMN_ANSWER_LABEL_HEIGHT +
    2 * answerPadding;
  const answerPanelCenter = answerPanel.viewportX + answerPanel.viewportW / 2;
  const answerRegionX = isLeftAligned
    ? answerPanel.viewportX
    : Math.max(0, answerPanelCenter - answerRegionWidth / 2);
  const answerRegionY = answerPanel.viewportY;
  const availableHorizontalPadding = answerRegionWidth - answerPanel.viewportW;
  const answerPanelHorizontalOffset = isLeftAligned
    ? Math.min(
        answerPadding,
        Math.max(
          0,
          availableHorizontalPadding - MINIMAL_SINGLE_COLUMN_ANSWER_PADDING
        )
      )
    : availableHorizontalPadding / 2;

  answerPanel.viewportX = answerRegionX + answerPanelHorizontalOffset;
  answerPanel.viewportY =
    answerRegionY + SINGLE_COLUMN_ANSWER_LABEL_HEIGHT + answerPadding;

  if (!isLeftAligned && answerRegionWidth > originalSectionWidth) {
    const offset = (answerRegionWidth - originalSectionWidth) / 2;

    section.children
      .filter(child => child !== answerPanel)
      .forEach(child => {
        child.viewportX += offset;
      });
  }

  const answerRegion = new HelpAnswerRegion(
    answerRegionY,
    answerRegionWidth,
    answerRegionHeight,
    answerIndex
  );

  answerRegion.viewportX = answerRegionX;
  section.add(answerRegion);
  section.sizeMeToFitChildren();
};
